<template>
  <header class="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-200">
    <nav class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-20">
        <!-- Logo -->
        <div class="flex items-center">
          <router-link :to="`/${currentLang()}`" class="flex items-center space-x-3">
            <div class="relative h-10 w-20">
              <!-- 自定义Logo图片 -->
              <img 
                v-if="!showPlaceholder.logo && hasCustomLogo"
                :src="logoPath" 
                :alt="companyName" 
                class="h-10 w-auto object-contain"
                @load="handleLogoLoad"
                @error="handleLogoError"
              />
              <!-- 占位图（当没有自定义logo或加载失败时） -->
              <ImagePlaceholder 
                v-else
                type="building" 
                width="40" 
                height="30" 
                gradient="blue"
                show-icon
                class="absolute inset-0"
              />
            </div>
            <div class="hidden sm:block flex-shrink-0 min-w-0 max-w-[320px]">
              <h1 
                class="font-bold text-gray-900 transition-all duration-300"
                :class="isChineseLocale ? 'text-xl' : 'text-sm leading-relaxed'"
              >
                {{ t('footer.company.name') }}
              </h1>
              <p v-if="isChineseLocale" class="text-[0.65rem] text-gray-600">{{ t('footer.company.englishName') }}</p>
            </div>
          </router-link>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden lg:flex flex-wrap items-center justify-center space-x-4 gap-y-1" style="min-width: 0;">
          <router-link 
            v-for="item in getNavigationPaths" 
            :key="item.name"
            :to="item.href"
            class="nav-item text-gray-700 hover:text-primary-600 px-2 py-2 text-medium  font-medium transition-colors whitespace-nowrap"
            :class="{ 'text-primary-600': $route.path === item.href }"
          >
            {{ $t(item.name) }}
          </router-link>
        </div>

        <!-- Right side actions -->
        <div class="flex items-center space-x-4">
          <!-- Search -->
          <button 
            @click="openSearch"
            class="p-2 text-gray-600 hover:text-primary-600 transition-colors"
          >
            <MagnifyingGlassIcon class="h-5 w-5" />
          </button>

          <!-- Language Switcher 切换语言 -->
          <LanguageSwitcher />
         

          <!-- Admin Login -->
          <!-- <a 
            v-if="!authStore.isAuthenticated"
            href="/login"
            target="_blank"
            class="hidden sm:block btn-primary text-sm"
          >
            管理登录
          </a>  -->
          
          <!-- User Menu -->
          <!-- <div v-else class="relative">
            <button 
              @click="toggleUserMenu"
              class="flex items-center space-x-2 p-2 text-gray-600 hover:text-primary-600 transition-colors"
            >
              <UserCircleIcon class="h-6 w-6" />
            </button>
            
            <!-- User Dropdown  用户下拉菜单 -->
            <!-- <div 
              v-if="showUserMenu"
              class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-200"
            >
              <router-link 
                to="/admin"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showUserMenu = false"
              >
                管理后台
              </router-link>
              <button 
                @click="handleLogout"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                退出登录
              </button>
            </div>
          </div> --> 

          <!-- Mobile menu button -->
          <button 
            @click="toggleMobileMenu"
            class="lg:hidden p-2 text-gray-600 hover:text-primary-600 transition-colors"
          >
            <Bars3Icon v-if="!showMobileMenu" class="h-6 w-6" />
            <XMarkIcon v-else class="h-6 w-6" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <div 
        v-if="showMobileMenu"
        class="lg:hidden border-t border-gray-200 py-4"
      >
        <div class="flex flex-col space-y-3">
          <router-link 
            v-for="item in getNavigationPaths" 
            :key="item.name"
            :to="item.href"
            class="text-gray-700 hover:text-primary-600 px-3 py-2 text-base font-medium transition-colors"
            :class="{ 'text-primary-600': $route.path === item.href }"
            @click="showMobileMenu = false"
          >
            {{ $t(item.name) }}
          </router-link>
          
          <!-- <div class="pt-4 border-t border-gray-200">
            <router-link 
              v-if="!authStore.isAuthenticated"
              to="/login"
              class="block btn-primary text-center text-sm"
              @click="showMobileMenu = false"
            >
              管理登录
            </router-link>
            <button 
              v-else
              @click="handleLogout"
              class="block w-full btn-secondary text-sm"
            >
              退出登录
            </button>
          </div> -->
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
// import { useAuthStore } from '@/stores/auth'
import { 
  MagnifyingGlassIcon, 
  // UserCircleIcon, 
  Bars3Icon, 
  XMarkIcon 
} from '@heroicons/vue/24/outline'
import ImagePlaceholder from '@/components/ui/ImagePlaceholder.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { useCurrentLang } from '@/utils/langUtils'
import { useBasicSettings } from '@/composables/useBasicSettings'
import { useI18n } from 'vue-i18n'


const route = useRoute()
const { t, locale } = useI18n()

// 判断是否为中文环境
const isChineseLocale = computed(() => {
  return ['zh-CN', 'zh-TW', 'zh'].includes(locale.value)
})

// 使用公共语言工具
const { currentLang } = useCurrentLang()

const showMobileMenu = ref(false)
const showUserMenu = ref(false)
// const currentLanguage = ref<'zh' | 'en'>('zh')
const showPlaceholder = ref<Record<string, boolean>>({
  logo: false
})

// 计算属性：获取带语言前缀的导航路径
const getNavigationPaths = computed(() => {
  const lang = currentLang()
  return [
    { name: 'nav.home', href: `/${lang}` },
    { name: 'nav.about', href: `/${lang}/about` },
    { name: 'nav.services', href: `/${lang}/services` },
    { name: 'nav.projects', href: `/${lang}/projects` },
    { name: 'nav.news', href: `/${lang}/news` },
    { name: 'nav.media', href: `/${lang}/gallery` },
    { name: 'nav.contact', href: `/${lang}/contact` },
  ]
})

// 计算导航项（响应式）
// const navigation = computed(() => [
//   { name: 'nav.home', href: buildPath('/') },
//   { name: 'nav.about', href: buildPath('/about') },
//   { name: 'nav.services', href: buildPath('/services') },
//   { name: 'nav.projects', href: buildPath('/projects') },
//   { name: 'nav.news', href: buildPath('/news') },
//   { name: 'nav.media', href: buildPath('/gallery') },
//   { name: 'nav.contact', href: buildPath('/contact') },
// ])

// 监听路由变化，确保导航链接随语言变化而更新
watch(() => route.params.lang, () => {
  // 当语言变化时，navigation 会自动重新计算
})

// 公司信息
const companyName = ref('')
const companyEnglishName = ref('')
const logoPath = ref('')

// 计算属性：是否有有效的自定义logo
const hasCustomLogo = computed(() => {
  return logoPath.value && 
         typeof logoPath.value === 'string' && 
         logoPath.value.trim() !== '' &&
         !showPlaceholder.value.logo
})

// const navigation = [
//   { name: '首页', href: '/' },
//   { name: '关于我们', href: '/about' },
//   { name: '业务范围', href: '/services' },
//   { name: '项目案例', href: '/projects' },
//   // { name: 'AI绘画', href: '/ai-drawing' },
//   { name: '新闻资讯', href: '/news' },
//   { name: '图片视频', href: '/gallery' },
//   { name: '联系我们', href: '/contact' },
// ]

// 使用 useBasicSettings composable
const { settings, fetchBasicSettings } = useBasicSettings()

// 获取站点设置
const fetchSiteSettings = async () => {
  try {
    // 获取当前语言
    const lang = (route.params.lang as string) || 'zh'
    const langCode = lang === 'zh' ? 'zh-CN' : lang // 转换为完整的语言代码
    
    // 前端页面使用当前语言获取翻译值
    await fetchBasicSettings(langCode)
    
    companyName.value = settings.value.site_name || '安徽艾尔工程建设有限公司'
    companyEnglishName.value = settings.value.site_english_name || 'ANHUI AIR ENGINEERING & CONSTRUCTION CO., LTD.'
    
    // 处理logo路径
    const logoValue = settings.value.logo
    
    if (logoValue && typeof logoValue === 'string' && logoValue.trim() !== '') {
      // 确保路径格式正确
      logoPath.value = logoValue.startsWith('/') ? logoValue : `/${logoValue}`
      showPlaceholder.value.logo = false
    } else {
      logoPath.value = ''
      showPlaceholder.value.logo = true
      console.log('⚠️ 没有自定义logo，显示占位图')
    }
  } catch (error) {
    console.error('💥 获取站点设置失败:', error)
    // 使用默认值
    companyName.value = '安徽艾尔工程建设有限公司'
    companyEnglishName.value = 'ANHUI AIR ENGINEERING & CONSTRUCTION CO., LTD.'
    logoPath.value = ''
    showPlaceholder.value.logo = true
  }
}

const openSearch = () => {
  // Emit event to open search modal
  document.dispatchEvent(new CustomEvent('open-search'))
}

// const toggleLanguage = () => {
//   currentLanguage.value = currentLanguage.value === 'zh' ? 'en' : 'zh'
//   // Implement language switching logic
// }

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

onMounted(() => {
  fetchSiteSettings()
})

// const toggleUserMenu = () => {
//   showUserMenu.value = !showUserMenu.value
// }

// 退出登录
// const handleLogout = () => {
//   authStore.logout()
//   showUserMenu.value = false
//   showMobileMenu.value = false
//   router.push('/')
// }

const handleLogoLoad = () => {
  // console.log('✅ Logo加载成功')
  showPlaceholder.value.logo = false
}

const handleLogoError = (event: Event) => {
  console.log('❌ 自定义Logo加载失败，显示占位图')
  showPlaceholder.value.logo = true
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}


// Close dropdowns when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>